/**
 * BottomNavigation 底部導航列共用元件
 *
 * 基於 MUI BottomNavigation 封裝，提供一致的 API。
 * 圖示不在此元件內 import，由使用端透過 items[].icon 傳入，方便各頁面自訂圖示。
 *
 * @example
 * // 使用端自行 import 需要的 icon，傳入 items
 * const navItems = [
 *   { label: '首頁', value: 'home', icon: <HomeIcon /> },
 *   { label: '我的', value: 'profile', icon: <PersonIcon /> },
 * ];
 * <BottomNavigation value={value} onChange={handleChange} items={navItems} />
 */

import * as React from "react";
import PropTypes from "prop-types";
import MuiBottomNavigation from "@mui/material/BottomNavigation";
import MuiBottomNavigationAction from "@mui/material/BottomNavigationAction";

const BottomNavigation = React.forwardRef((props, ref) => {
	const { value, onChange, items, sx, ...other } = props;

	return (
		<MuiBottomNavigation ref={ref} value={value} onChange={onChange} sx={sx} {...other}>
			{items.map(item => (
				<MuiBottomNavigationAction key={item.value} label={item.label} value={item.value} icon={item.icon} />
			))}
		</MuiBottomNavigation>
	);
});

BottomNavigation.displayName = "BottomNavigation";

BottomNavigation.propTypes = {
	/** 目前選中的項目 value，需與 items 中某一項的 value 一致 */
	value: PropTypes.string,
	/** 切換選項時觸發，(event, newValue) => void */
	onChange: PropTypes.func.isRequired,
	/** 導航項目陣列，每項需含 label、value、icon（icon 由使用端傳入，不共用） */
	items: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			icon: PropTypes.node.isRequired,
		}),
	).isRequired,
	/** MUI sx 樣式物件，可覆寫容器樣式 */
	sx: PropTypes.object,
	/** 是否永遠顯示 label（true 時未選中也會顯示文字） */
	showLabels: PropTypes.bool,
};

export default BottomNavigation;
